import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Trash2, FileSpreadsheet, FileBadge, AlertCircle, CheckCircle2, Upload as UploadIcon, Target as TargetIcon, GitBranch, ShieldCheck, Repeat, Users, Crown, Network, ListTree, Mail, Copy, History, Pencil, BookOpen, Sparkles, ClipboardList, ShieldAlert } from "lucide-react";
import DataQualityPanel from "../components/DataQualityPanel";
import { toast } from "sonner";
import LogFrameDiagram from "../components/LogFrameDiagram";
import BulkIndicatorImport from "../components/import/BulkIndicatorImport";
import SmartMergeUpload from "../components/import/SmartMergeUpload";
import FormsTab from "../components/FormsTab";
import IntegrationsTab from "../components/IntegrationsTab";

export default function ProgramDetail() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [tab, setTab] = useState("indicators");
  const [smartMergeOpen, setSmartMergeOpen] = useState(false);
  const [publishedShareUrl, setPublishedShareUrl] = useState(null);  // Phase 3.48 — "Fill Form" CTA

  const load = async () => {
    const [p, i, u] = await Promise.all([
      api.get(`/programs/${pid}`),
      api.get(`/programs/${pid}/indicators`),
      api.get(`/programs/${pid}/uploads`),
    ]);
    setProgram(p.data);
    setIndicators(i.data);
    setUploads(u.data);
    // Phase 3.48 — surface a "Fill Form" CTA only when there's at least
    // one published form with an active public share. We probe the first
    // published form's shares; if any is public, mode-public + non-revoked,
    // we use its token as the deep-link destination.
    try {
      const formsRes = await api.get(`/programs/${pid}/forms`);
      const published = (formsRes.data || []).filter((f) => f.status === "published");
      for (const f of published) {
        const sharesRes = await api.get(`/forms/${f.id}/shares`);
        // /forms/{fid}/shares wraps the array in `{items: [...]}`; defend
        // against either shape.
        const arr = Array.isArray(sharesRes.data) ? sharesRes.data : (sharesRes.data?.items || []);
        const live = arr.find(
          (s) => s.mode === "public" && !s.revoked
            && (!s.expires_at || new Date(s.expires_at) > new Date()),
        );
        if (live) {
          setPublishedShareUrl(`/forms/${live.token}`);
          return;
        }
      }
      setPublishedShareUrl(null);
    } catch {
      setPublishedShareUrl(null);
    }
  };

  useEffect(() => { load(); }, [pid]);

  if (!program) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="mb-8">
        <Link to="/" className="label-overline hover:text-slate-900" data-testid="back-link">← All programs</Link>
        <div className="flex items-end justify-between gap-6 mt-3">
          <div>
            <h1 className="font-heading text-4xl tracking-tight font-semibold">{program.name}</h1>
            <p className="text-slate-600 mt-2 max-w-2xl">{program.description}</p>
          </div>
          <div className="flex items-center gap-2">
            {publishedShareUrl && (
              <a
                href={publishedShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fill-form-cta"
                className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md px-3 py-2 text-sm transition-colors"
              >
                <ClipboardList className="w-4 h-4" /> Fill Form
              </a>
            )}
            <Button
              onClick={() => setSmartMergeOpen(true)}
              className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
              data-testid="upload-new-data-button"
            >
              <Sparkles className="w-4 h-4 mr-1" /> Upload new data
            </Button>
            <Link to={`/programs/${pid}/upload`}>
              <Button variant="outline" data-testid="upload-data-button"><UploadIcon className="w-4 h-4 mr-1"/> Legacy upload</Button>
            </Link>
            <Link to={`/programs/${pid}/theory-of-change`}>
              <Button variant="outline" data-testid="view-toc-button"><BookOpen className="w-4 h-4 mr-1"/> Theory of Change</Button>
            </Link>
            <Link to={`/programs/${pid}/report`}>
              <Button variant="outline" data-testid="view-report-button"><FileBadge className="w-4 h-4 mr-1"/> View report</Button>
            </Link>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 rounded-sm">
          <TabsTrigger value="indicators" data-testid="tab-indicators">Indicators ({indicators.length})</TabsTrigger>
          <TabsTrigger
            value="forms"
            data-testid="tab-forms"
            className="data-[state=active]:text-emerald-700"
          >
            <ClipboardList className="w-3.5 h-3.5 mr-1 text-emerald-600" />
            Forms
          </TabsTrigger>
          <TabsTrigger value="logframe" data-testid="tab-logframe">LogFrame</TabsTrigger>
          <TabsTrigger value="vocabulary" data-testid="tab-vocabulary">Vocabulary</TabsTrigger>
          <TabsTrigger value="uploads" data-testid="tab-uploads">Uploads ({uploads.length})</TabsTrigger>
          <TabsTrigger value="data-quality" data-testid="tab-data-quality">
            <ShieldAlert className="w-3.5 h-3.5 mr-1" />
            Data Quality
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
        </TabsList>

        <TabsContent value="indicators" className="mt-6">
          <IndicatorsTab pid={pid} indicators={indicators} reload={load} />
        </TabsContent>
        <TabsContent value="forms" className="mt-6">
          <FormsTab pid={pid} indicators={indicators} />
        </TabsContent>
        <TabsContent value="logframe" className="mt-6">
          <LogFrameTab pid={pid} indicators={indicators} reload={load} />
        </TabsContent>
        <TabsContent value="vocabulary" className="mt-6">
          <VocabularyTab pid={pid} indicators={indicators} reload={load} />
        </TabsContent>
        <TabsContent value="uploads" className="mt-6">
          <UploadsTab pid={pid} uploads={uploads} reload={load} />
        </TabsContent>
        <TabsContent value="data-quality" className="mt-6">
          <ProgramDataQualityTab pid={pid} />
        </TabsContent>
        <TabsContent value="integrations" className="mt-6">
          <IntegrationsTab pid={pid} reload={load} />
        </TabsContent>
        <TabsContent value="members" className="mt-6">
          <MembersTab pid={pid} />
        </TabsContent>
      </Tabs>

      {program?.owner_id === user?.id && (
        <DangerZone
          program={program}
          onDeleted={() => navigate("/")}
        />
      )}

      <SmartMergeUpload
        pid={pid}
        open={smartMergeOpen}
        onClose={() => setSmartMergeOpen(false)}
        onCommitted={() => load()}
      />
    </Layout>
  );
}

// Phase 3.32 — Owner-only "Danger Zone" with type-to-confirm deletion.
function DangerZone({ program, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/programs/${program.id}`);
      const purged = r.data.purged || {};
      const totals = Object.values(purged).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Deleted "${program.name}" and purged ${totals} linked records.`);
      setOpen(false);
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete the program");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-12 border-2 border-rose-200 rounded-md bg-rose-50/60 p-5" data-testid="program-danger-zone">
      <div className="flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="font-heading text-base font-semibold text-rose-900">Danger Zone</p>
          <p className="text-sm text-rose-800/80 mt-1">
            Deleting this program permanently removes it along with all indicators, data rows, uploads, reports, forms, share links, and dashboard shares. This action cannot be undone.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => { setConfirm(""); setOpen(true); }}
          className="border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-900 shrink-0"
          data-testid="delete-program-btn"
        >
          <Trash2 className="w-4 h-4 mr-1.5" /> Delete Program
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="delete-program-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-900">
              <ShieldAlert className="w-5 h-5" /> Delete program
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold text-slate-900">{program.name}</span>?
              This action is permanent and will wipe all associated indicators, data rows, and reports.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label className="text-xs text-slate-600">
              Type <span className="font-mono font-semibold text-rose-700">DELETE</span> to confirm:
            </Label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE"
              className="font-mono"
              data-testid="delete-program-confirm"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={doDelete}
              disabled={busy || confirm !== "DELETE"}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="delete-program-confirm-btn"
            >
              {busy ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IndicatorsTab({ pid, indicators, reload }) {
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", unit: "", data_type: "numeric", description: "", parent_indicator_id: "" });
  const [tags, setTags] = useState([{ key: "Level", value: "Output" }]);
  const [axes, setAxes] = useState([]);

  const addTag = () => setTags([...tags, { key: "", value: "" }]);
  const updateTag = (i, k, v) => {
    const t = [...tags]; t[i] = { ...t[i], [k]: v }; setTags(t);
  };
  const removeTag = (i) => setTags(tags.filter((_, idx) => idx !== i));

  const addAxis = () => setAxes([...axes, { key: "", values: "", required: true }]);
  const updateAxis = (i, field, val) => {
    const next = [...axes]; next[i] = { ...next[i], [field]: val }; setAxes(next);
  };
  const removeAxis = (i) => setAxes(axes.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      code: form.code,
      unit: form.unit,
      data_type: form.data_type,
      description: form.description,
      tags: tags.filter(t => t.key && t.value),
      disaggregation_axes: axes
        .filter(a => a.key.trim())
        .map(a => ({
          key: a.key.trim(),
          values: a.values ? a.values.split(",").map(v => v.trim()).filter(Boolean) : [],
          required: !!a.required,
        })),
    };
    if (form.parent_indicator_id) payload.parent_indicator_id = form.parent_indicator_id;
    await api.post(`/programs/${pid}/indicators`, payload);
    setForm({ name: "", code: "", unit: "", data_type: "numeric", description: "", parent_indicator_id: "" });
    setTags([{ key: "Level", value: "Output" }]);
    setAxes([]);
    setOpen(false);
    reload();
  };

  const remove = async (id) => {
    await api.delete(`/indicators/${id}`);
    reload();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="label-overline">Indicator catalog</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-import-button"
          >
            <Sparkles className="w-4 h-4 mr-1 text-emerald-600" /> Bulk import
          </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="new-indicator-button"><Plus className="w-4 h-4 mr-1"/> Add indicator</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New indicator</DialogTitle>
              <DialogDescription>Define a measurable indicator with a code, type, optional tags, and disaggregation axes.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="new-indicator-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">Code</Label>
                  <Input value={form.code} onChange={(e)=>setForm({...form, code: e.target.value})} required className="mt-2" placeholder="EDU-OUT-1" data-testid="indicator-code-input" />
                </div>
                <div>
                  <Label className="label-overline">Unit</Label>
                  <Input value={form.unit} onChange={(e)=>setForm({...form, unit: e.target.value})} className="mt-2" placeholder="children, %, USD" data-testid="indicator-unit-input" />
                </div>
              </div>
              <div>
                <Label className="label-overline">Name</Label>
                <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required className="mt-2" data-testid="indicator-name-input" />
              </div>
              <div>
                <Label className="label-overline">Data type</Label>
                <Select value={form.data_type} onValueChange={(v)=>setForm({...form, data_type: v})}>
                  <SelectTrigger className="mt-2" data-testid="indicator-datatype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="numeric">Numeric</SelectItem>
                    <SelectItem value="text">Text (qualitative)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">Description</Label>
                <Textarea value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-2" data-testid="indicator-description-input" />
              </div>
              <div>
                <Label className="label-overline">Parent indicator <span className="text-slate-400 normal-case tracking-normal">(optional — for LogFrame nesting)</span></Label>
                <Select value={form.parent_indicator_id || "none"} onValueChange={(v)=>setForm({...form, parent_indicator_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-2" data-testid="indicator-parent-select"><SelectValue placeholder="None — top-level indicator"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None — top-level indicator</SelectItem>
                    {indicators.map((ind) => (
                      <SelectItem key={ind.id} value={ind.id}>{ind.code} — {ind.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">Tags</Label>
                <div className="space-y-2 mt-2">
                  {tags.map((t, i) => (
                    <div key={i} className="flex gap-2">
                      <Input placeholder="Key (e.g. Level)" value={t.key} onChange={(e)=>updateTag(i, "key", e.target.value)} data-testid={`tag-key-${i}`} />
                      <Input placeholder="Value (e.g. Output)" value={t.value} onChange={(e)=>updateTag(i, "value", e.target.value)} data-testid={`tag-value-${i}`} />
                      <Button type="button" variant="ghost" size="icon" onClick={()=>removeTag(i)}><Trash2 className="w-4 h-4"/></Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addTag} data-testid="add-tag-button">+ Add tag</Button>
                </div>
              </div>
              <div>
                <Label className="label-overline">Disaggregation axes <span className="text-slate-400 normal-case tracking-normal">— optional. Leave blank for no slicing.</span></Label>
                <div className="space-y-2 mt-2" data-testid="axis-editor">
                  {axes.map((a, i) => (
                    <div key={i} className="border border-slate-200 rounded-sm p-3 space-y-2">
                      <div className="flex gap-2">
                        <Input placeholder="Axis key (e.g. gender)" value={a.key} onChange={(e)=>updateAxis(i, "key", e.target.value)} data-testid={`axis-key-${i}`} />
                        <label className="flex items-center gap-1 text-xs text-slate-600 shrink-0 px-2">
                          <input type="checkbox" checked={!!a.required} onChange={(e)=>updateAxis(i, "required", e.target.checked)} data-testid={`axis-required-${i}`}/>
                          Required
                        </label>
                        <Button type="button" variant="ghost" size="icon" onClick={()=>removeAxis(i)}><Trash2 className="w-4 h-4"/></Button>
                      </div>
                      <Input
                        placeholder="Allowed values, comma-separated (e.g. Female, Male, Non-binary). Leave blank for free-text."
                        value={a.values}
                        onChange={(e)=>updateAxis(i, "values", e.target.value)}
                        data-testid={`axis-values-${i}`}
                      />
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addAxis} data-testid="add-axis-button">+ Add axis</Button>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-indicator-button">Save indicator</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {bulkOpen && (
        <BulkIndicatorImport
          pid={pid}
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onImported={() => { setBulkOpen(false); reload(); }}
        />
      )}

      {indicators.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="indicators-empty">
          No indicators yet. Add your first indicator with a code, data type and tags.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="indicators-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 text-white text-left">
                <th className="px-4 py-3 font-medium w-32">Code</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium w-28">Type</th>
                <th className="px-4 py-3 font-medium w-24">Unit</th>
                <th className="px-4 py-3 font-medium w-44">Target</th>
                <th className="px-4 py-3 font-medium">Tags</th>
                <th className="px-4 py-3 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((i) => (
                <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs">{i.code}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span>{i.name}</span>
                      {i.parent_indicator_id && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500" title="Has parent indicator">
                          <GitBranch className="w-3 h-3"/> child
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-xs uppercase tracking-wider">{i.data_type}</Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{i.unit || "—"}</td>
                  <td className="px-4 py-3">
                    {i.target ? (
                      <TargetSummary target={i.target} unit={i.unit} />
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {i.tags.map((t, idx) => (
                        <Badge key={idx} variant="secondary" className="text-[10px] tracking-wide">{t.key}: {t.value}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <IndicatorEditDialog indicator={i} indicators={indicators} onSaved={reload} />
                      <IndicatorAuditDialog pid={pid} indicator={i} />
                      <IndicatorTargetDialog indicator={i} onSaved={reload} />
                      <Button variant="ghost" size="icon" onClick={()=>remove(i.id)} data-testid={`delete-indicator-${i.code}`}><Trash2 className="w-4 h-4 text-slate-500"/></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IndicatorEditDialog({ indicator, indicators, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: indicator.name || "",
    description: indicator.description || "",
    unit: indicator.unit || "",
    parent_indicator_id: indicator.parent_indicator_id || "",
  });
  const [saving, setSaving] = useState(false);

  // Reset form when indicator changes / dialog reopens
  const handle = (v) => {
    setOpen(v);
    if (v) {
      setForm({
        name: indicator.name || "",
        description: indicator.description || "",
        unit: indicator.unit || "",
        parent_indicator_id: indicator.parent_indicator_id || "",
      });
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        unit: form.unit,
      };
      if (form.parent_indicator_id) {
        payload.parent_indicator_id = form.parent_indicator_id;
      } else if (indicator.parent_indicator_id) {
        payload.clear_parent = true;
      }
      await api.patch(`/indicators/${indicator.id}`, payload);
      toast.success("Indicator updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const eligibleParents = (indicators || []).filter((p) => p.id !== indicator.id);

  return (
    <Dialog open={open} onOpenChange={handle}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`edit-indicator-${indicator.code}`} title="Edit indicator">
          <Pencil className="w-4 h-4 text-slate-500"/>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`edit-indicator-dialog-${indicator.code}`}>
        <DialogHeader>
          <DialogTitle>Edit indicator — {indicator.code}</DialogTitle>
          <DialogDescription>Update the name, description, unit and parent. Code and data type are immutable.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs text-slate-500">
            <div><span className="label-overline">Code</span><div className="font-mono mt-1">{indicator.code}</div></div>
            <div><span className="label-overline">Data type</span><div className="mt-1 capitalize">{indicator.data_type}</div></div>
          </div>
          <div>
            <Label className="label-overline">Name</Label>
            <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2" data-testid={`edit-indicator-name-${indicator.code}`}/>
          </div>
          <div>
            <Label className="label-overline">Unit</Label>
            <Input value={form.unit} onChange={(e)=>setForm({...form, unit: e.target.value})} className="mt-2" placeholder="children, %, USD" data-testid={`edit-indicator-unit-${indicator.code}`}/>
          </div>
          <div>
            <Label className="label-overline">Description</Label>
            <Textarea value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-2" rows={3} data-testid={`edit-indicator-description-${indicator.code}`}/>
          </div>
          <div>
            <Label className="label-overline">Parent indicator <span className="text-slate-400 normal-case tracking-normal">(optional)</span></Label>
            <Select
              value={form.parent_indicator_id || "none"}
              onValueChange={(v)=>setForm({...form, parent_indicator_id: v === "none" ? "" : v})}
            >
              <SelectTrigger className="mt-2" data-testid={`edit-indicator-parent-${indicator.code}`}>
                <SelectValue placeholder="None — top-level indicator"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None — top-level indicator</SelectItem>
                {eligibleParents.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            className="bg-slate-900 hover:bg-slate-800 text-white"
            onClick={save}
            disabled={saving}
            data-testid={`save-edit-indicator-${indicator.code}`}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IndicatorAuditDialog({ pid, indicator }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${pid}/indicators/audit-log`, { params: { indicator_id: indicator.id } });
      setEntries(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load history");
    } finally {
      setLoading(false);
    }
  };

  const handle = (v) => {
    setOpen(v);
    if (v && entries === null) load();
  };

  const renderValue = (field, val) => {
    if (val == null) return <span className="italic text-slate-400">— empty —</span>;
    if (field === "target") {
      if (typeof val !== "object") return String(val);
      if (val.mode === "single") return `${val.value} ${indicator.unit || ""} (single target)`;
      if (val.mode === "per_period") return `Per-period: ${Object.entries(val.by_period || {}).map(([k, v]) => `${k}=${v}`).join(", ")}`;
      return JSON.stringify(val);
    }
    if (field === "disaggregation_axes" && Array.isArray(val)) {
      return val.length ? val.map((a) => `${a.key}${a.values?.length ? ` (${a.values.length} values)` : ""}`).join(", ") : <span className="italic text-slate-400">none</span>;
    }
    if (field === "parent_indicator_id") {
      return val || <span className="italic text-slate-400">— top-level —</span>;
    }
    if (field === "_deleted" && typeof val === "object") {
      return `${val.code || ""} — ${val.name || ""}`;
    }
    return typeof val === "object" ? JSON.stringify(val) : String(val);
  };

  const fieldLabel = (f) => ({
    name: "Name",
    description: "Description",
    parent_indicator_id: "Parent",
    target: "Target",
    disaggregation_axes: "Disaggregation axes",
    _deleted: "Indicator deleted",
  }[f] || f);

  return (
    <Dialog open={open} onOpenChange={handle}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`indicator-history-${indicator.code}`} title="Edit history">
          <History className="w-4 h-4 text-slate-500" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid={`indicator-history-dialog-${indicator.code}`}>
        <DialogHeader>
          <DialogTitle>Schema history — {indicator.code}</DialogTitle>
          <DialogDescription>
            Every rename, target change, parent move, and disaggregation update — plus who and when. Newest first.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto pr-1 space-y-3" data-testid={`indicator-history-list-${indicator.code}`}>
          {loading && <p className="text-sm text-slate-500">Loading…</p>}
          {!loading && entries && entries.length === 0 && (
            <p className="text-sm text-slate-500" data-testid={`indicator-history-empty-${indicator.code}`}>
              No edits yet. As soon as someone changes the name, target, parent, description, or disaggregation, it will appear here.
            </p>
          )}
          {!loading && entries && entries.map((e) => (
            <div key={e.id} className="border border-slate-200 rounded-sm p-3 bg-slate-50">
              <div className="flex items-baseline justify-between text-xs text-slate-600">
                <span className="font-medium text-slate-800">{e.by_user_name || e.by_user_email || "Unknown"}</span>
                <span className="font-mono">{new Date(e.ts).toLocaleString()}</span>
              </div>
              <div className="mt-2">
                <span className="label-overline">{fieldLabel(e.field)}</span>
                {e.action === "delete" && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-red-100 text-red-700 rounded-sm">Deleted</span>
                )}
              </div>
              {e.field !== "_deleted" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-xs">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Before</p>
                    <div className="bg-white border border-slate-200 p-2 rounded-sm text-slate-500 line-through whitespace-pre-wrap break-words">
                      {renderValue(e.field, e.prev_value)}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">After</p>
                    <div className="bg-white border border-emerald-200 p-2 rounded-sm text-slate-800 whitespace-pre-wrap break-words">
                      {renderValue(e.field, e.new_value)}
                    </div>
                  </div>
                </div>
              )}
              {e.field === "_deleted" && (
                <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 p-2 rounded-sm">
                  {renderValue(e.field, e.prev_value)}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TargetSummary({ target, unit }) {
  if (!target) return <span className="text-xs text-slate-400">—</span>;
  if (target.mode === "single") {
    return (
      <span className="inline-flex items-center gap-1 text-xs">
        <TargetIcon className="w-3 h-3 text-slate-500"/>
        <span className="font-mono">{Number(target.value).toLocaleString()}</span>
        {unit && <span className="text-slate-500">{unit}</span>}
      </span>
    );
  }
  const periods = Object.keys(target.by_period || {});
  const total = Object.values(target.by_period || {}).reduce((a, b) => a + Number(b || 0), 0);
  return (
    <span className="inline-flex items-center gap-1 text-xs" title={periods.map(p => `${p}: ${target.by_period[p]}`).join("\n")}>
      <TargetIcon className="w-3 h-3 text-slate-500"/>
      <span className="font-mono">{Number(total).toLocaleString()}</span>
      <span className="text-slate-500">across {periods.length} periods</span>
    </span>
  );
}

function IndicatorTargetDialog({ indicator, onSaved }) {
  const [open, setOpen] = useState(false);
  const initial = indicator.target || { mode: "single", value: "", by_period: {} };
  const [mode, setMode] = useState(initial.mode);
  const [value, setValue] = useState(initial.value ?? "");
  const [periods, setPeriods] = useState(
    Object.entries(initial.by_period || {}).length
      ? Object.entries(initial.by_period).map(([period, v]) => ({ period, value: String(v) }))
      : [{ period: "", value: "" }]
  );
  const [saving, setSaving] = useState(false);

  const updatePeriod = (idx, field, val) => {
    const next = [...periods];
    next[idx] = { ...next[idx], [field]: val };
    setPeriods(next);
  };
  const addPeriod = () => setPeriods([...periods, { period: "", value: "" }]);
  const removePeriod = (idx) => setPeriods(periods.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      let target = null;
      if (mode === "single") {
        if (value === "" || isNaN(Number(value))) {
          toast.error("Enter a numeric target value");
          setSaving(false);
          return;
        }
        target = { mode: "single", value: Number(value), by_period: {} };
      } else {
        const by = {};
        for (const p of periods) {
          if (!p.period.trim()) continue;
          const n = Number(p.value);
          if (isNaN(n)) {
            toast.error(`Invalid number for ${p.period}`);
            setSaving(false);
            return;
          }
          by[p.period.trim()] = n;
        }
        if (Object.keys(by).length === 0) {
          toast.error("Add at least one period target");
          setSaving(false);
          return;
        }
        target = { mode: "per_period", by_period: by };
      }
      await api.patch(`/indicators/${indicator.id}`, { target });
      toast.success("Target saved");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save target");
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.patch(`/indicators/${indicator.id}`, { clear_target: true });
      toast.success("Target removed");
      setOpen(false);
      onSaved();
    } finally { setSaving(false); }
  };

  if (indicator.data_type !== "numeric") {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`set-target-${indicator.code}`} title="Set target">
          <TargetIcon className="w-4 h-4 text-slate-500"/>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Target — {indicator.code}</DialogTitle>
          <DialogDescription>Set a numeric goal so donors can see progress at a glance.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-slate-600">
            Set a goal so donors can see progress at a glance. Choose <strong>single</strong> for a fixed end-of-program goal, or <strong>per period</strong> for milestone-based programs (e.g. 200 in Q1, 250 in Q2).
          </p>
          <div>
            <Label className="label-overline">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-2" data-testid="target-mode-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single — one total target</SelectItem>
                <SelectItem value="per_period">Per period — one target per reporting period</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "single" ? (
            <div>
              <Label className="label-overline">Target value {indicator.unit && <span className="text-slate-400 normal-case tracking-normal">({indicator.unit})</span>}</Label>
              <Input
                type="number"
                value={value}
                onChange={(e)=>setValue(e.target.value)}
                placeholder="e.g. 1000"
                className="mt-2 font-mono"
                data-testid="target-value-input"
              />
            </div>
          ) : (
            <div>
              <Label className="label-overline">Per-period targets</Label>
              <div className="space-y-2 mt-2">
                {periods.map((p, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input placeholder="Period (e.g. Q1-2025)" value={p.period} onChange={(e)=>updatePeriod(idx, "period", e.target.value)} data-testid={`target-period-${idx}`} />
                    <Input type="number" placeholder="Target" value={p.value} onChange={(e)=>updatePeriod(idx, "value", e.target.value)} className="w-32 font-mono" data-testid={`target-period-value-${idx}`} />
                    <Button type="button" variant="ghost" size="icon" onClick={()=>removePeriod(idx)}><Trash2 className="w-4 h-4"/></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addPeriod} data-testid="add-target-period-button">+ Add period</Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {indicator.target && (
            <Button variant="outline" onClick={clear} disabled={saving} data-testid="clear-target-button">Remove target</Button>
          )}
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-target-button">
            {saving ? "Saving…" : "Save target"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function descendantsOf(id, childrenByParent) {
  const out = [];
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop();
    for (const c of (childrenByParent[cur] || [])) {
      out.push(c.id);
      stack.push(c.id);
    }
  }
  return out;
}

function flattenTree(roots, childrenByParent) {
  // DFS to produce a list of {ind, depth} preserving parent → child order
  const out = [];
  const visit = (nodes, depth) => {
    for (const n of nodes) {
      out.push({ ind: n, depth });
      const kids = childrenByParent[n.id] || [];
      if (kids.length) visit(kids, depth + 1);
    }
  };
  visit(roots, 0);
  return out;
}

function LogFrameTab({ pid, indicators, reload }) {
  const [view, setView] = useState("diagram");
  const [breakdownByIid, setBreakdownByIid] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/programs/${pid}/report`);
        if (cancelled) return;
        const m = {};
        for (const b of data.breakdown || []) m[b.indicator.id] = b;
        setBreakdownByIid(m);
      } catch {
        // Diagram falls back to showing nodes without progress bars — not fatal.
      }
    })();
    return () => { cancelled = true; };
  }, [pid, indicators.length]);

  // Build tree of indicators grouped by parent_indicator_id
  const indById = Object.fromEntries(indicators.map((i) => [i.id, i]));
  const childrenByParent = {};
  const roots = [];
  for (const i of indicators) {
    const p = i.parent_indicator_id;
    if (p && indById[p]) {
      if (!childrenByParent[p]) childrenByParent[p] = [];
      childrenByParent[p].push(i);
    } else {
      roots.push(i);
    }
  }
  const setParent = async (childId, newParentId) => {
    if (newParentId === childId) {
      toast.error("An indicator cannot be its own parent");
      return;
    }
    try {
      if (newParentId) {
        await api.patch(`/indicators/${childId}`, { parent_indicator_id: newParentId });
      } else {
        await api.patch(`/indicators/${childId}`, { clear_parent: true });
      }
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set parent");
    }
  };
  if (indicators.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="logframe-empty">
        No indicators yet. Create indicators in the Indicators tab to build a LogFrame hierarchy.
      </div>
    );
  }
  const flat = flattenTree(roots, childrenByParent);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant={view === "diagram" ? "default" : "outline"}
          size="sm"
          className={view === "diagram" ? "bg-slate-900 text-white" : ""}
          onClick={() => setView("diagram")}
          data-testid="logframe-view-diagram"
        >
          <Network className="w-3.5 h-3.5 mr-1" /> Diagram
        </Button>
        <Button
          variant={view === "tree" ? "default" : "outline"}
          size="sm"
          className={view === "tree" ? "bg-slate-900 text-white" : ""}
          onClick={() => setView("tree")}
          data-testid="logframe-view-tree"
        >
          <ListTree className="w-3.5 h-3.5 mr-1" /> Tree · edit
        </Button>
      </div>
      {view === "diagram" ? (
        <LogFrameDiagram indicators={indicators} breakdownByIid={breakdownByIid} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="logframe-tree">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-slate-500"/>
            <p className="label-overline">Logical framework — parent / child indicators</p>
            <span className="ml-auto text-xs text-slate-500">{roots.length} top-level · {indicators.length - roots.length} nested</span>
          </div>
          <ul className="p-3">
            {flat.map(({ ind, depth }) => {
              const ineligible = new Set([ind.id, ...descendantsOf(ind.id, childrenByParent)]);
              const eligibleParents = indicators.filter((i) => !ineligible.has(i.id));
              return (
                <li key={ind.id} className="my-1" data-testid={`logframe-node-${ind.code}`}>
                  <div
                    className="flex items-center gap-3 py-2 px-3 rounded-sm hover:bg-slate-50 border border-transparent hover:border-slate-200"
                    style={{ marginLeft: `${depth * 24}px` }}
                  >
                    <span className="font-mono text-[10px] text-slate-500 w-20 truncate shrink-0">{ind.code}</span>
                    <span className="flex-1 min-w-0 truncate text-sm">{ind.name}</span>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider shrink-0">{ind.data_type}</Badge>
                    <Select
                      value={ind.parent_indicator_id || "__root__"}
                      onValueChange={(v) => setParent(ind.id, v === "__root__" ? null : v)}
                    >
                      <SelectTrigger className="w-48 h-8 text-xs" data-testid={`logframe-parent-${ind.code}`}>
                        <SelectValue placeholder="Move under…"/>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__root__">— Top-level —</SelectItem>
                        {eligibleParents.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function VocabularyTab({ pid, indicators, reload }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState({}); // {key: canonicalValue}

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${pid}/standardization-suggestions`);
      setSuggestions(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadSuggestions(); }, [pid]);

  const keyFor = (s) => `${s.indicator_id}|${s.axis_key}|${s.raw_value}`;
  const indById = Object.fromEntries(indicators.map((i) => [i.id, i]));

  const act = async (s, endpoint) => {
    const key = keyFor(s);
    const canonical = picks[key] || s.allowed_values?.[0];
    if (!canonical) {
      toast.error("Pick a canonical value");
      return;
    }
    try {
      const { data } = await api.post(`/programs/${pid}/vocabulary/${endpoint}`, {
        indicator_id: s.indicator_id,
        axis_key: s.axis_key,
        raw_value: s.raw_value,
        canonical,
      });
      const label = endpoint === "approve"
        ? `Approved "${s.raw_value}" → ${canonical} (${data.rows_revalidated} rows revalidated)`
        : `Replaced "${s.raw_value}" in ${data.rows_replaced} row(s)`;
      toast.success(label);
      await loadSuggestions();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  if (loading) {
    return <div className="text-center text-slate-500 py-10" data-testid="vocab-loading">Loading suggestions…</div>;
  }
  if (suggestions.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="vocab-empty">
        <ShieldCheck className="w-5 h-5 mx-auto mb-2 text-emerald-600"/>
        <p className="font-medium text-slate-700">Vocabulary is clean.</p>
        <p className="text-xs mt-1">No off-vocabulary disaggregation values detected across your uploads.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="vocab-panel">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600"/>
        <p className="label-overline">Vocabulary review</p>
        <span className="ml-auto text-xs text-slate-500">{suggestions.length} off-vocab value{suggestions.length > 1 ? "s" : ""}</span>
      </div>
      <p className="px-4 pt-3 text-xs text-slate-500 max-w-3xl">
        These disaggregation values were found in your uploads but don't match the allowed list.
        <strong className="text-slate-700"> Approve as alias</strong> permanently maps the raw value so future uploads auto-normalize it.
        <strong className="text-slate-700"> Replace in data</strong> mutates the stored rows to use the canonical value (use when the raw value was a typo).
      </p>
      <ul className="divide-y divide-slate-100">
        {suggestions.map((s) => {
          const key = keyFor(s);
          const ind = indById[s.indicator_id] || {};
          const pick = picks[key] || s.allowed_values?.[0] || "";
          return (
            <li key={key} className="px-4 py-4 flex flex-wrap items-center gap-3" data-testid={`vocab-row-${s.axis_key}-${s.raw_value}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-[10px] text-slate-500 uppercase tracking-wider">{s.indicator_code || ind.code}</span>
                  <span className="label-overline text-slate-500">{s.axis_key}</span>
                </div>
                <div className="mt-1">
                  <span className="font-mono text-sm bg-amber-50 border border-amber-200 text-amber-900 px-2 py-0.5 rounded">"{s.raw_value}"</span>
                  <span className="text-xs text-slate-500 ml-2">seen {s.occurrences}×</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Allowed: {s.allowed_values?.join(", ") || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-500">→</span>
                <Select value={pick} onValueChange={(v) => setPicks({ ...picks, [key]: v })}>
                  <SelectTrigger className="w-40 h-9 text-xs" data-testid={`vocab-canonical-${s.axis_key}-${s.raw_value}`}>
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    {(s.allowed_values || []).map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => act(s, "approve")} data-testid={`vocab-approve-${s.axis_key}-${s.raw_value}`}>
                  <ShieldCheck className="w-3 h-3 mr-1"/> Approve alias
                </Button>
                <Button size="sm" variant="outline" onClick={() => act(s, "replace")} data-testid={`vocab-replace-${s.axis_key}-${s.raw_value}`}>
                  <Repeat className="w-3 h-3 mr-1"/> Replace in data
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Phase 2 — Program-level Data Quality tab. Fetches /api/programs/{pid}/data-quality.
function ProgramDataQualityTab({ pid }) {
  const [dq, setDq] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/programs/${pid}/data-quality`)
      .then(({ data }) => setDq(data))
      .catch(() => setDq({ summary: { outliers: 0, duplicates: 0, missing: 0, row_count: 0, indicator_count: 0 }, outliers: [], duplicates: [], missing: [] }))
      .finally(() => setLoading(false));
  }, [pid]);
  return (
    <DataQualityPanel
      dq={dq}
      loading={loading}
      title="Data Quality Intelligence"
      subtitle="Outliers, duplicates, and missing periods detected in this program's uploaded data."
    />
  );
}


function MembersTab({ pid }) {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${pid}/members`);
      setData(data);
      if (data.my_role === "admin") {
        const inv = await api.get(`/programs/${pid}/invites`);
        setPending(inv.data || []);
      } else {
        setPending([]);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pid]);

  const sendInvite = async (e) => {
    e?.preventDefault?.();
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setSubmitting(true);
    try {
      const { data: resp } = await api.post(`/programs/${pid}/members`, { email: trimmed, role });
      if (resp.kind === "added_existing") {
        toast.success(`Added ${trimmed} as ${role}`);
      } else if (resp.kind === "invite_pending") {
        if (resp.invite?.email_sent) {
          toast.success(`Invite emailed to ${trimmed}`);
        } else {
          toast.warning(
            `Invite created for ${trimmed} but email failed to send. Copy the link from the Pending section.`,
            { duration: 7000 },
          );
        }
      }
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not invite user");
    } finally {
      setSubmitting(false);
    }
  };

  const updateRole = async (uid, newRole) => {
    try {
      await api.patch(`/programs/${pid}/members/${uid}`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update role");
    }
  };

  const remove = async (uid, email) => {
    if (!window.confirm(`Remove ${email} from this program?`)) return;
    try {
      await api.delete(`/programs/${pid}/members/${uid}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  const revokeInvite = async (token, em) => {
    if (!window.confirm(`Revoke pending invite for ${em}?`)) return;
    try {
      await api.delete(`/programs/${pid}/invites/${token}`);
      toast.success("Invite revoked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not revoke");
    }
  };

  const copyLink = async (url, em) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Magic link for ${em} copied to clipboard`);
    } catch {
      toast.error("Could not copy. Long-press the link to copy manually.");
    }
  };

  if (loading || !data) {
    return <div className="text-center text-slate-500 py-10" data-testid="members-loading">Loading members…</div>;
  }
  const canManage = data.my_role === "admin";

  return (
    <div className="space-y-6" data-testid="members-tab">
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500"/>
          <p className="label-overline">Program members</p>
          <span className="ml-auto text-xs text-slate-500">
            You are <strong className="text-slate-700">{data.my_role}</strong>
          </span>
        </div>
        <ul className="divide-y divide-slate-100" data-testid="members-list">
          {data.owner && (
            <li className="px-4 py-3 flex items-center gap-3" data-testid="member-owner">
              <Crown className="w-4 h-4 text-amber-500 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{data.owner.name}</div>
                <div className="text-xs text-slate-500 truncate">{data.owner.email}</div>
              </div>
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] uppercase tracking-wider">Owner</Badge>
            </li>
          )}
          {data.members.map((m) => (
            <li key={m.user_id} className="px-4 py-3 flex items-center gap-3" data-testid={`member-${m.user_id}`}>
              <Users className="w-4 h-4 text-slate-400 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{m.name || "—"}</div>
                <div className="text-xs text-slate-500 truncate">{m.email || m.user_id}</div>
              </div>
              {canManage ? (
                <Select value={m.role} onValueChange={(v) => updateRole(m.user_id, v)}>
                  <SelectTrigger className="w-28 h-8 text-xs" data-testid={`member-role-${m.user_id}`}>
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{m.role}</Badge>
              )}
              {canManage && (
                <Button variant="ghost" size="icon" onClick={() => remove(m.user_id, m.email)} data-testid={`member-remove-${m.user_id}`}>
                  <Trash2 className="w-4 h-4 text-slate-500"/>
                </Button>
              )}
            </li>
          ))}
          {data.members.length === 0 && !canManage && (
            <li className="px-4 py-6 text-center text-xs text-slate-500">No other members yet.</li>
          )}
        </ul>
      </div>

      {canManage && (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="invite-card">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Plus className="w-4 h-4 text-slate-500"/>
            <p className="label-overline">Invite by email</p>
            <span className="ml-auto text-xs text-slate-500">
              Existing users are added immediately. New users get a magic-link sign-up email.
            </span>
          </div>
          <form onSubmit={sendInvite} className="p-4 flex flex-col sm:flex-row gap-2" data-testid="invite-form">
            <Input
              type="email"
              placeholder="teammate@org.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1"
              data-testid="invite-email-input"
              required
            />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-32" data-testid="invite-role-select">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" disabled={submitting} data-testid="invite-submit">
              {submitting ? "Sending…" : "Send invite"}
            </Button>
          </form>
        </div>
      )}

      {canManage && pending.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="pending-invites">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-500"/>
            <p className="label-overline">Pending invites · {pending.length}</p>
          </div>
          <ul className="divide-y divide-slate-100">
            {pending.map((it) => (
              <li key={it.token} className="px-4 py-3 flex items-center gap-3" data-testid={`pending-invite-${it.token}`}>
                <Mail className="w-4 h-4 text-slate-400 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{it.email}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {it.email_sent ? "Email sent" : <span className="text-amber-700">Email failed — share link manually</span>}
                    {" · expires "}
                    {new Date(it.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{it.role}</Badge>
                <Button variant="outline" size="sm" onClick={() => copyLink(it.invite_url, it.email)} data-testid={`copy-invite-${it.token}`}>
                  <Copy className="w-3.5 h-3.5"/>
                </Button>
                <Button variant="ghost" size="icon" onClick={() => revokeInvite(it.token, it.email)} data-testid={`revoke-invite-${it.token}`}>
                  <Trash2 className="w-4 h-4 text-slate-500"/>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UploadAuditDialog({ pid, upload }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${pid}/upload-audit-log`, { params: { upload_id: upload.id } });
      setEntries(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load audit log");
    } finally {
      setLoading(false);
    }
  };

  const onChange = (v) => {
    setOpen(v);
    if (v) load();
  };

  const actionLabel = {
    upload_parsed: "Upload parsed",
    mapped: "Columns mapped",
    remapped: "Columns re-mapped",
    auto_clean: "Auto-clean run",
    row_edited: "Row edited",
    row_deleted: "Row deleted",
    row_restored: "Row restored",
    upload_deleted: "Upload deleted",
  };
  const actionTone = {
    upload_parsed: "bg-slate-100 text-slate-700 border border-slate-200",
    mapped: "bg-sky-50 text-sky-800 border border-sky-200",
    remapped: "bg-amber-50 text-amber-900 border border-amber-200",
    auto_clean: "bg-emerald-50 text-emerald-900 border border-emerald-200",
    row_edited: "bg-violet-50 text-violet-900 border border-violet-200",
    row_deleted: "bg-red-50 text-red-800 border border-red-200",
    row_restored: "bg-sky-50 text-sky-800 border border-sky-200",
    upload_deleted: "bg-red-50 text-red-800 border border-red-200",
  };

  const summary = (e) => {
    const d = e.details || {};
    switch (e.action) {
      case "upload_parsed":
        return `${d.row_count || 0} rows · ${(d.columns || []).length} columns`;
      case "mapped":
      case "remapped":
        return `${d.clean || 0} clean · ${d.dirty || 0} dirty${d.preserved_fixes ? ` · ${d.preserved_fixes} manual fix${d.preserved_fixes === 1 ? "" : "es"} preserved` : ""}${d.superseded ? ` · ${d.superseded} superseded` : ""}`;
      case "auto_clean":
        return `${d.auto_fixed || 0} auto-fixed · ${d.suggested || 0} suggestions · ${d.still_dirty || 0} still dirty`;
      case "row_edited": {
        const before = d.before || {};
        const after = d.after || {};
        return `${before.indicator_code || "?"}·${before.period || "?"} → ${after.indicator_code || "?"}·${after.period || "?"} · ${after.value_raw}`;
      }
      case "row_deleted":
        return `${e.indicator_code || "?"}·${d.period || "?"} · ${d.value_raw}`;
      case "row_restored":
        return `${e.indicator_code || "?"}·${d.period || "?"}`;
      case "upload_deleted":
        return `${d.row_count || 0} rows removed`;
      default:
        return "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`audit-upload-${upload.id}`}
          title="See every mapping, auto-clean and row-edit decision for this upload"
        >
          <History className="w-3.5 h-3.5 mr-1" /> History
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`audit-upload-dialog-${upload.id}`}>
        <DialogHeader>
          <DialogTitle>Audit history — {upload.filename}</DialogTitle>
          <DialogDescription>
            Every mapping, re-mapping, auto-clean, row edit, restore or delete for this dataset — newest first.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-slate-500 text-sm py-6">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="text-slate-500 text-sm border border-dashed border-slate-200 rounded-sm p-6 text-center">
            No audit entries yet. Events created before this feature was shipped are not retroactively logged.
          </div>
        ) : (
          <ol className="space-y-2 text-sm">
            {entries.map((e) => (
              <li
                key={e.id}
                className="border border-slate-200 rounded-sm p-3"
                data-testid={`audit-entry-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className={`px-2 py-0.5 rounded-sm text-[11px] uppercase tracking-wider ${actionTone[e.action] || "bg-slate-100 text-slate-700 border border-slate-200"}`}>
                    {actionLabel[e.action] || e.action}
                  </span>
                  <span className="text-[11px] text-slate-500 shrink-0">
                    {new Date(e.at).toLocaleString()}
                  </span>
                </div>
                <p className="mt-2 text-slate-700 text-[13px]">{summary(e)}</p>
                <p className="mt-1 text-[11px] text-slate-500">
                  by <span className="font-medium text-slate-700">{e.user_name || "system"}</span>
                  {e.user_email && <span className="text-slate-400"> · {e.user_email}</span>}
                </p>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UploadsTab({ pid, uploads, reload }) {
  const [deletingId, setDeletingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const doDelete = async (u) => {
    try {
      setBusy(true);
      await api.delete(`/uploads/${u.id}`);
      toast.success(`Deleted ${u.filename}`);
      setDeletingId(null);
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  if (uploads.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="uploads-empty">
        No data uploaded yet. Click <em>Upload data</em> to ingest a CSV or Excel file.
      </div>
    );
  }
  const pending = uploads.find((u) => u.id === deletingId);
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="uploads-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-900 text-white text-left">
            <th className="px-4 py-3 font-medium">File</th>
            <th className="px-4 py-3 font-medium w-32">Rows</th>
            <th className="px-4 py-3 font-medium w-40">Status</th>
            <th className="px-4 py-3 font-medium w-44">Uploaded</th>
            <th className="px-4 py-3 w-40"></th>
          </tr>
        </thead>
        <tbody>
          {uploads.map((u) => (
            <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-4 py-3 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-slate-500"/> <span className="font-mono text-xs">{u.filename}</span>
              </td>
              <td className="px-4 py-3 tabular">{u.row_count}</td>
              <td className="px-4 py-3">
                {u.status === "pending_mapping" ? (
                  <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">Awaiting mapping</Badge>
                ) : (
                  <div className="flex gap-2">
                    <span className="text-emerald-700 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> {u.clean_count || 0} clean</span>
                    <span className="text-red-700 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3"/> {u.dirty_count || 0} dirty</span>
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-slate-600 text-xs">
                {new Date(u.uploaded_at).toLocaleString()}<br/>
                <span className="text-slate-400">{u.uploaded_by_name}</span>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-2">
                  <UploadAuditDialog pid={pid} upload={u} />
                  <Link to={`/programs/${pid}/uploads/${u.id}/validate`}>
                    <Button variant="outline" size="sm" data-testid={`validate-upload-${u.id}`}>Open</Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    data-testid={`delete-upload-${u.id}`}
                    onClick={() => setDeletingId(u.id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Dialog open={!!deletingId} onOpenChange={(v) => !v && setDeletingId(null)}>
        <DialogContent data-testid="delete-upload-dialog">
          <DialogHeader>
            <DialogTitle>Delete upload?</DialogTitle>
            <DialogDescription>
              This permanently removes <span className="font-mono">{pending?.filename}</span> and all
              {" "}{(pending?.clean_count || 0) + (pending?.dirty_count || 0)} data rows produced from it.
              Aggregated numbers on the report will recompute instantly.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)} disabled={busy}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => pending && doDelete(pending)}
              disabled={busy}
              data-testid="confirm-delete-upload"
            >
              {busy ? "Deleting…" : "Delete upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
